import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, PenLine, Check, Lock, Printer, Trash2, FileSignature, ExternalLink,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { OFFER, offerActive, discounted, eur } from "@/lib/offer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "essential",
    name: "Nex Essential",
    setup: 990,
    monthly: 19,
    desc: "Sito su misura 5 pagine, mobile perfetto, Google Business Profile, form e mappa.",
  },
  {
    id: "pro",
    name: "Nex Pro Direct",
    setup: 1990,
    monthly: 29,
    desc: "Sito premium 10 pagine, prenotazioni senza commissioni, menù/camere, SEO locale, ottimizzazione foto e testi forniti.",
  },
  {
    id: "tailored",
    name: "Nex Tailored",
    setup: 2990,
    monthly: 49,
    desc: "Design 100% custom, booking engine / PMS, multilingua IT/EN/DE/FR, supporto e aggiornamenti 12 mesi.",
  },
];


function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const inking = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const pos = (e) => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    };
  };

  const down = (e) => {
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = "#0E0C0B";
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    inking.current = true;
  };
  const move = (e) => {
    if (!inking.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  };
  const up = () => {
    inking.current = false;
    if (ref.current) onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={ref}
        width={640}
        height={190}
        data-testid="proposta-signature-canvas"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        className="w-full cursor-crosshair rounded-xl border border-line bg-white"
        style={{ touchAction: "none" }}
      />
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-faded">
          {hasInk ? "Firma acquisita." : "Firma qui con dito o mouse."}
        </p>
        <button
          type="button"
          onClick={clear}
          data-testid="proposta-signature-clear"
          className="inline-flex items-center gap-1.5 text-xs text-stone transition-colors duration-300 hover:text-terracotta"
        >
          <Trash2 size={12} /> Cancella
        </button>
      </div>
    </div>
  );
}

function PinGate({ prospectId, onOk }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/proposta/${prospectId}/verify`, { code });
      sessionStorage.setItem(`nex_proposta_access_${prospectId}`, "1");
      onOk();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-md px-5 pt-28 pb-24">
      <div className="rounded-3xl border border-line bg-panel p-8 text-center">
        <Lock size={26} className="mx-auto text-terracotta" />
        <h1 className="mt-4 font-serif text-2xl tracking-tight text-bone">Area riservata</h1>
        <p className="mt-2 text-sm text-stone">
          Inserisci il codice di accesso ricevuto da Nex Studio Design per aprire la proposta.
        </p>
        <form onSubmit={submit} className="mt-6">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Codice di accesso"
            data-testid="proposta-pin-input"
            className="w-full rounded-xl border border-line bg-ink px-4 py-3 text-center text-sm tracking-[0.3em] text-bone focus:border-terracotta focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="proposta-pin-submit"
            className="mt-4 w-full rounded-full bg-terracotta px-6 py-3.5 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a] disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="mx-auto animate-spin" /> : "Apri la proposta"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Proposal() {
  const { prospectId } = useParams();
  const [unlocked, setUnlocked] = useState(
    sessionStorage.getItem(`nex_proposta_access_${prospectId}`) === "1"
  );
  const [prospect, setProspect] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [planId, setPlanId] = useState("pro");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/prospects/${prospectId}`);
      setProspect(data);
      if (data.email) setClientEmail(data.email);
      try {
        const s = await axios.get(`${API}/proposta/${prospectId}/signed`);
        setSigned(s.data);
      } catch (e) {
        /* non ancora firmata */
      }
    } catch (e) {
      setNotFound(true);
    }
  };

  useEffect(() => {
    if (unlocked) load();
  }, [unlocked, prospectId]);

  const sign = async (e) => {
    e.preventDefault();
    if (!signature) {
      toast.error("Aggiungi la firma prima di inviare.");
      return;
    }
    setSigning(true);
    try {
      const plan = PLANS.find((p) => p.id === planId);
      const { data } = await axios.post(`${API}/proposta/${prospectId}/sign`, {
        client_name: clientName,
        client_email: clientEmail || null,
        plan_id: planId,
        signature,
      });
      setSigned(data);
      toast.success("Proposta firmata e salvata!");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Firma non riuscita");
    } finally {
      setSigning(false);
    }
  };

  if (!unlocked) return <PinGate prospectId={prospectId} onOk={() => setUnlocked(true)} />;

  if (notFound)
    return (
      <main className="min-h-screen bg-ink pt-28 pb-24 text-center">
        <p className="font-serif text-2xl text-bone">Proposta non trovata.</p>
        <Link to="/strumenti" className="mt-4 inline-block text-terracotta underline">
          Torna all'area di lavoro
        </Link>
      </main>
    );

  if (!prospect)
    return (
      <main className="min-h-screen bg-ink pt-28 pb-24 text-center">
        <Loader2 size={26} className="mx-auto animate-spin text-terracotta" />
      </main>
    );

  const plan = PLANS.find((p) => p.id === planId);

  return (
    <main className="min-h-screen bg-ink pb-24">
      <header className="no-print glass sticky top-0 z-40 border-b border-line">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark size={28} />
            <span className="font-serif text-base tracking-tight text-bone">Nex Studio Design</span>
          </div>
          <Link
            to="/strumenti"
            data-testid="proposta-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 font-accent text-[10px] uppercase tracking-[0.2em] text-stone transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
          >
            <ArrowLeft size={13} /> Area di lavoro
          </Link>
        </div>
      </header>

      <div className="print-area mx-auto max-w-4xl px-5 pt-10 sm:px-8">
        {signed ? (
          <div className="rounded-3xl border border-cypress/50 bg-panel p-8 lg:p-10">
            <div className="flex items-center gap-3">
              <FileSignature size={24} className="text-terracotta" />
              <h1 className="font-serif text-3xl tracking-tight text-bone">Proposta firmata</h1>
            </div>
            <p className="mt-3 text-sm text-stone">
              Sottoscritta digitalmente da <strong className="text-bone">{signed.client_name}</strong> il{" "}
              {new Date(signed.signed_at).toLocaleDateString("it-IT", {
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-line bg-ink p-4">
                <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-faded">Attività</p>
                <p className="mt-1 text-sm font-semibold text-bone">{signed.prospect_name}</p>
              </div>
              <div className="rounded-xl border border-line bg-ink p-4">
                <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-faded">Piano</p>
                <p className="mt-1 text-sm font-semibold text-bone">{signed.plan_name}</p>
              </div>
              <div className="rounded-xl border border-line bg-ink p-4">
                <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-faded">Economia</p>
                <p className="mt-1 text-sm font-semibold text-bone">
                  {eur(signed.setup_price)} + {eur(signed.monthly_price)}/mese
                </p>
              </div>
            </div>
            <img
              src={signed.signature}
              alt="Firma digitale"
              className="mt-6 h-24 rounded-xl border border-line bg-white"
              data-testid="proposta-signed-image"
            />
            <button
              onClick={() => window.print()}
              data-testid="proposta-print-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 font-accent text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
            >
              <Printer size={14} /> Stampa / Salva PDF
            </button>
          </div>
        ) : (
          <form onSubmit={sign} data-testid="proposta-form">
            <p className="font-accent text-[11px] uppercase tracking-[0.32em] text-terracotta">
              Proposta commerciale
            </p>
            <h1 className="mt-4 font-serif text-3xl font-medium tracking-tight text-bone sm:text-4xl">
              Nuova vetrina digitale per{" "}
              <em className="italic text-terracotta">{prospect.name}</em>
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-stone">
              Documento riservato preparato per {prospect.name} ({prospect.city}). Definisce lavori,
              costi di realizzazione e mantenimento. Validità 30 giorni.
            </p>
            {prospect.preview_url && (
              <a
                href={prospect.preview_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="proposta-preview-link"
                className="group mt-6 flex items-center justify-between gap-4 rounded-2xl border border-terracotta/40 bg-terracotta/10 px-5 py-4 transition-colors duration-300 hover:bg-terracotta/20"
              >
                <span>
                  <span className="block font-accent text-[10px] uppercase tracking-[0.24em] text-terracotta">
                    Anteprima già pronta
                  </span>
                  <span className="mt-1 block font-serif text-lg text-bone">Guarda il nuovo sito di {prospect.name}</span>
                  <span className="mt-0.5 block text-xs text-stone">Si apre in una nuova scheda · navigabile da telefono</span>
                </span>
                <ExternalLink size={18} className="flex-none text-terracotta transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            )}

            <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
              {PLANS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlanId(p.id)}
                  data-testid={`proposta-plan-${p.id}`}
                  className={`rounded-2xl border p-5 text-left transition-all duration-300 ${
                    planId === p.id
                      ? "border-terracotta bg-panel-2"
                      : "border-line bg-panel hover:border-line-strong"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-lg tracking-tight text-bone">{p.name}</p>
                    {planId === p.id && <Check size={16} className="text-terracotta" />}
                  </div>
                  <p className="mt-2 font-serif text-2xl tracking-tight text-bone">
                    {eur(discounted(p.setup))}
                    <span className="ml-2 font-sans text-xs text-stone">+ {eur(p.monthly)}/mese</span>
                  </p>
                  {offerActive() && (
                    <p className="mt-1 text-xs text-faded">
                      <span className="line-through">{eur(p.setup)}</span>
                      <span className="ml-2 text-ochre">−{OFFER.pct}% lancio</span>
                    </p>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-stone">{p.desc}</p>
                </button>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-line bg-panel p-6 text-sm leading-relaxed text-stone">
              <p className="font-accent text-[11px] uppercase tracking-[0.24em] text-terracotta">
                Condizioni economiche — piano {plan.name}
              </p>
              <ul className="mt-4 space-y-2.5">
                <li>
                  <strong className="text-bone">Realizzazione: {eur(discounted(plan.setup))}</strong> una tantum,
                  pagabili 50% all'ordine e 50% alla pubblicazione.
                  {offerActive() && (
                    <>
                      {" "}Prezzo di listino {eur(plan.setup)}: sconto lancio del {OFFER.pct}% riservato ai contratti
                      firmati entro il {OFFER.deadlineLabel}.
                    </>
                  )}
                </li>
                <li>
                  <strong className="text-bone">Gestione: {eur(plan.monthly)}/mese</strong> — hosting,
                  aggiornamenti tecnici, sicurezza, piccole modifiche mensili. Canone minimo 12 mesi,
                  fatturato dal mese successivo alla pubblicazione.
                </li>
                <li>
                  <strong className="text-bone">Dominio</strong> registrato a nome del cliente (~{eur(12)}/anno
                  direttamente al registrar) — il cliente ne resta unico proprietario.
                </li>
                <li>Email professionale configurabile sul dominio (servizio terzo, da ~0 a 6 €/mese).</li>
                <li>IVA dove dovuta. Foto e materiali non recuperabili online forniti dal cliente.</li>
                <li>Tempi: 3-5 settimane dal materiale. Garanzia: 30 giorni di correzioni gratuite.</li>
              </ul>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                required
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Nome e cognome del titolare *"
                data-testid="proposta-client-name"
                className="w-full rounded-xl border border-line bg-panel px-4 py-3 text-sm text-bone placeholder:text-faded focus:border-terracotta focus:outline-none"
              />
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="Email del cliente"
                data-testid="proposta-client-email"
                className="w-full rounded-xl border border-line bg-panel px-4 py-3 text-sm text-bone placeholder:text-faded focus:border-terracotta focus:outline-none"
              />
            </div>

            <div className="mt-6">
              <p className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.24em] text-terracotta">
                <PenLine size={13} /> Firma digitale
              </p>
              <div className="mt-3">
                <SignaturePad onChange={setSignature} />
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 text-sm text-stone">
              <input
                type="checkbox"
                required
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="proposta-accept-checkbox"
                className="mt-1 h-4 w-4 accent-[#E05A47]"
              />
              Dichiaro di accettare la proposta sopra descritta nei prezzi, nei tempi e nelle condizioni.
            </label>

            <button
              type="submit"
              disabled={signing}
              data-testid="proposta-sign-button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-terracotta px-7 py-4 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a] disabled:opacity-60"
            >
              {signing ? <Loader2 size={16} className="animate-spin" /> : <FileSignature size={16} />}
              {signing ? "Salvataggio…" : "Firma e invia la proposta"}
            </button>
            <p className="mt-4 text-center text-xs text-faded" data-testid="proposta-support-contact">
              Domande prima di firmare? Scrivici a{" "}
              <a href="mailto:assistenza@nexstudiodesign.it" className="text-stone underline-offset-2 hover:underline">
                assistenza@nexstudiodesign.it
              </a>
            </p>
          </form>
        )}
      </div>
    </main>
  );
}